<template>
  <v-layout column>
    <v-dialog
      v-model="editUserDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="600"
      persistent
      @keydown.esc="editUserDialogOpened = false"
    >
      <v-card block flat v-if="selectedUser">
        <v-card-title>
          <span class="title">Edit User</span>
          <v-spacer />
          <span class="caption error--text">*Required</span>
        </v-card-title>
        <v-card-text>
          <v-layout column>
            <v-layout class="text-xs-left">
              <v-flex xs6 class="pr-2">
                <div>
                  Firm Access
                  <v-tooltip bottom>
                    <v-icon slot="activator" size="16">info</v-icon>
                    <span
                      >Please contact Soraban if you want to change the user's
                      access to/from basic or group-based</span
                    >
                  </v-tooltip>
                </div>
                <v-select
                  placeholder="Select an Access"
                  v-model="selectedUser.access"
                  :items="changeableAccessRoles"
                  item-text="name"
                  item-value="value"
                  required
                  solo
                  :disabled="
                    !changeableAccessRoles
                      .map(role => role.value)
                      .includes(selectedUser.access)
                  "
                  class="default-input"
                />
              </v-flex>
              <v-flex xs6 class="pl-2" v-if="selectedUser.access === 'group'">
                <v-layout class="text-xs-left">
                  <span class="error--text">*</span>
                  Assigned Group
                  <v-tooltip top>
                    <v-icon size="15" slot="activator" class="ml-2"
                      >information</v-icon
                    >
                    <span
                      >The user will be able to view & manage all clients within
                      the group. Please make sure the group has clients before
                      assigning the staff</span
                    >
                  </v-tooltip>
                </v-layout>
                <v-select
                  type="text"
                  :items="currentUserFirm.firm_groups"
                  placeholder="Select group"
                  v-model="selectedUser.groupId"
                  solo
                  class="default-input"
                  item-text="name"
                  item-value="id"
                  required
                />
              </v-flex>
            </v-layout>
            <v-layout class="text-xs-left">
              <v-flex xs6 class="pr-2">
                <div><span class="error--text">*</span>First Name</div>
                <v-text-field
                  name="first-name"
                  placeholder="Jack"
                  type="text"
                  v-model="selectedUser.fname"
                  required
                  :rules="fieldRules"
                  solo
                  class="default-input"
                />
              </v-flex>
              <v-flex xs6 class="pl-2">
                <div><span class="error--text">*</span>Last Name</div>
                <v-text-field
                  name="last-name"
                  placeholder="Smith"
                  type="text"
                  v-model="selectedUser.lname"
                  required
                  :rules="fieldRules"
                  solo
                  class="default-input"
                />
              </v-flex>
            </v-layout>
            <v-layout class="text-xs-left">
              <v-flex xs6 class="pr-2">
                <div>
                  Email
                  <v-tooltip bottom>
                    <v-icon size="16" slot="activator">information</v-icon>
                    <span
                      >This does NOT change where the emails are sent
                      <i>from</i>.<br />It only changes access to the account
                      and where email notifications will be sent
                      <i>to</i>.</span
                    >
                  </v-tooltip>
                </div>
                <v-text-field
                  type="text"
                  v-model="selectedUser.email"
                  solo
                  class="default-input"
                />
              </v-flex>
              <v-flex xs6 class="pl-2">
                <div>Phone number</div>
                <v-text-field
                  name="phone-number"
                  type="phone"
                  mask="phone"
                  placeholder="Optional"
                  v-model="selectedUser.phone_number"
                  solo
                  class="default-input"
                />
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            :disabled="!userSavable"
            @click="saveUser()"
          >
            Save
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="editUserDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="removeUserConfirmationDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="600"
      @keydown.esc="removeUserConfirmationDialogOpened = false"
    >
      <v-card block flat v-if="selectedUser">
        <v-card-title class="title"> Confirmation </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="error" :value="true" outline>
            Are you sure you want to remove the user {{ selectedUser.name }}?
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="primary text-none" @click="removeUser()"> Yes </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="removeUserConfirmationDialogOpened = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="userInviteDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="700"
      persistent
      @keydown.esc="userInviteDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title class="pb-0">
          <span class="title">Invite New User</span>
          <v-spacer />
          <span class="caption error--text">*Required</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form1" v-model="userValid">
            <v-layout column>
              <v-flex class="mb-4">
                <v-alert
                  :value="true"
                  outline
                  block
                  class="text-xs-left"
                  type="info"
                >
                  'Admin' access can do anything you can do, and can change firm
                  settings & preferences. 'Full' has access to all clients, as
                  well as adding new clients{{
                    currentUserFirm.has_firm_groups
                      ? ". 'Group-based' have access to clients within the same group"
                      : ''
                  }}, and 'Basic' only has access to clients that you or other
                  admin assigned on Overview page.
                </v-alert>
              </v-flex>
              <v-layout>
                <v-flex xs6 class="pr-2">
                  <div class="text-xs-left">
                    <span class="error--text">*</span>
                    Firm Access
                  </div>
                  <v-select
                    type="text"
                    :items="
                      currentUserFirm.has_firm_groups
                        ? accessRoles
                        : accessRolesWithoutGroup
                    "
                    v-model="newUserAccess"
                    placeholder="Select Access"
                    solo
                    class="default-input"
                    item-text="name"
                    item-value="value"
                    required
                  />
                </v-flex>
                <v-flex xs6 class="pl-2" v-if="newUserAccess === 'basic'">
                  <v-layout class="text-xs-left">
                    <span class="error--text">*</span>
                    Assigned Clients
                    <v-tooltip top>
                      <v-icon size="15" slot="activator" class="ml-2"
                        >information</v-icon
                      >
                      <span
                        >Firm user with basic access need at least one client
                        assigned.</span
                      >
                    </v-tooltip>
                  </v-layout>
                  <v-autocomplete
                    type="text"
                    :items="clientsMapped"
                    placeholder="Select Clients to Assign"
                    v-model="newUserAssignedClientIds"
                    solo
                    small-chips
                    multiple
                    class="default-input"
                    item-text="full_name"
                    item-value="id"
                    required
                  >
                    <template slot="selection" slot-scope="data">
                      <v-chip
                        small
                        v-if="
                          newUserAssignedClientIds.indexOf(data.item.id) === 0
                        "
                      >
                        <span>{{ data.item.full_name }}</span>
                      </v-chip>
                      <span
                        v-if="
                          newUserAssignedClientIds.indexOf(data.item.id) === 1
                        "
                        class="grey--text caption"
                        >(+{{ newUserAssignedClientIds.length - 1 }} other{{
                          newUserAssignedClientIds.length > 2 ? 's' : ''
                        }})</span
                      >
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex xs6 class="pl-2" v-else-if="newUserAccess === 'group'">
                  <v-layout class="text-xs-left">
                    <span class="error--text">*</span>
                    Assigned Group
                    <v-tooltip top>
                      <v-icon size="15" slot="activator" class="ml-2"
                        >information</v-icon
                      >
                      <span
                        >The user will be able to view & manage all clients
                        within the group. Please make sure the group has clients
                        before assigning the staff</span
                      >
                    </v-tooltip>
                  </v-layout>
                  <v-select
                    type="text"
                    :items="currentUserFirm.firm_groups"
                    placeholder="Select group"
                    v-model="newUserGroupId"
                    solo
                    class="default-input"
                    item-text="name"
                    item-value="id"
                    required
                  />
                </v-flex>
              </v-layout>

              <v-layout class="text-xs-left">
                <v-flex xs6 class="pr-2">
                  <div>
                    <span class="error--text">*</span>
                    First Name
                  </div>
                  <v-text-field
                    name="first-name"
                    label="Jack"
                    type="text"
                    v-model="newUserFirstName"
                    required
                    :rules="fieldRules"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs6 class="pl-2">
                  <div>
                    <span class="error--text">*</span>
                    Last Name
                  </div>
                  <v-text-field
                    name="last-name"
                    label="Smith"
                    type="text"
                    v-model="newUserLastName"
                    required
                    :rules="fieldRules"
                    solo
                    class="default-input"
                  />
                </v-flex>
              </v-layout>

              <v-layout class="text-xs-left">
                <v-flex xs6 class="pr-2">
                  <div>
                    <span class="error--text">*</span>
                    Email
                  </div>
                  <v-text-field
                    name="email"
                    label="email@gmail.com"
                    type="text"
                    v-model="newUserEmail"
                    required
                    :rules="emailRules"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs6 class="pl-2">
                  <div>Phone number</div>
                  <v-text-field
                    name="phone-number"
                    type="phone"
                    mask="phone"
                    placeholder="Optional"
                    v-model="newUserPhoneNumber"
                    solo
                    class="default-input"
                  />
                </v-flex>
              </v-layout>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            @click="inviteUser()"
            :disabled="!newUserSavable"
          >
            Invite
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="userInviteDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-layout align-center wrap>
      <div class="font-weight-bold mx-2">Search</div>
      <v-flex sm4>
        <v-text-field
          v-model="userSearch"
          class="default-input subheading"
          solo
          hide-details
          placeholder="Search for user"
        />
      </v-flex>
      <v-spacer />
      <sc-button-group data-onboarding="true">
        <sc-button
          type="secondary"
          @click="openInviteClientDialog()"
          data-id="invite-firm-users-btn"
          >Invite Firm User</sc-button
        >
      </sc-button-group>
    </v-layout>
    <v-data-table
      :headers="userHeaders"
      :items="firmUsers"
      :search="userSearch"
      :loading="tableLoading || loading"
      :pagination.sync="userPagination"
      class="text-xs-left"
    >
      <template slot="headerCell" slot-scope="props">
        <span>
          {{ props.header.text }}
        </span>
      </template>
      <template slot="items" slot-scope="props">
        <tr :key="props.index" slot="header">
          <td class="text-xs-left">
            {{ props.item.name }}
          </td>
          <td class="text-xs-left">
            {{ props.item.email }}
          </td>
          <td class="text-xs-left">
            <span v-if="props.item.access === 'group'">
              Group:
              <v-chip slot="activator" small>
                <span class="caption">{{
                  get(props, 'item.group.name', '')
                }}</span>
              </v-chip>
            </span>
            <span v-else>{{ getAccessRole(props.item) }}</span>
          </td>
          <td>
            <v-layout align-center>
              <v-menu
                transition="slide-y-transition"
                bottom
                nudge-bottom="30"
                class="my-1"
              >
                <v-icon slot="activator"> more_vert </v-icon>
                <v-list>
                  <v-list-tile @click="openEditUserDialog(props.item)">
                    <v-list-tile-title> Edit User </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile @click="sendSignInLink(props.item.id)">
                    <v-list-tile-title> Send Sign-in Link </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile @click="openRemoveUserDialog(props.item)">
                    <v-list-tile-title> Remove User </v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-layout>
</template>

<script>
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { mapGetters, mapState } from 'vuex';
import { emailRules } from '@/constants/rules';

export default {
  name: 'FirmUsersTab',
  props: {
    currentUserFirm: {
      type: Object,
    },
    firmUsers: {
      type: Array,
    },
    loading: {
      type: Boolean,
    },
  },
  data() {
    return {
      tableLoading: false,
      newUserGroupId: null,
      newUserAccess: null,
      newUserAssignedClientIds: [],
      newUserFirstName: null,
      newUserLastName: null,
      newUserEmail: null,
      newUserPhoneNumber: null,
      userPagination: {
        sortBy: 'full_name',
        rowsPerPage: 10,
        descending: false,
        page: 1,
        options: [
          {
            text: '10 rows per page',
            value: 10,
          },
          {
            text: '20 rows per page',
            value: 20,
          },
          {
            text: '30 rows per page',
            value: 30,
          },
        ],
      },
      userHeaders: [
        {
          text: 'Full Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '25%',
        },
        {
          text: 'Email',
          align: 'left',
          sortable: true,
          value: 'email',
          width: '30%',
        },
        {
          text: 'Access',
          align: 'left',
          sortable: false,
          value: 'access',
          width: '25%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          value: 'source',
          width: '5%',
        },
      ],
      userValid: false,
      fieldRules: [v => !!v || 'This field is required'],
      emailRules,
      accessRoles: [
        {
          name: 'Admin',
          value: 'partner',
        },
        {
          name: 'Full',
          value: 'full',
        },
        {
          name: 'Group-based',
          value: 'group',
        },
        {
          name: 'Basic',
          value: 'basic',
        },
      ],
      changeableAccessRoles: [
        {
          name: 'Admin',
          value: 'partner',
        },
        {
          name: 'Full',
          value: 'full',
        },
      ],
      accessRolesWithoutGroup: [
        {
          name: 'Admin',
          value: 'partner',
        },
        {
          name: 'Full',
          value: 'full',
        },
        {
          name: 'Basic',
          value: 'basic',
        },
      ],
      editUserDialogOpened: false,
      userSearch: null,
      removeUserConfirmationDialogOpened: false,
      userInviteDialogOpened: false,
      selectedUser: null,
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),
    clientsMapped() {
      return [
        ...new Set(
          this.companies
            .map(company => company.user)
            .map(o => JSON.stringify(o)),
        ),
      ]
        .map(string => JSON.parse(string))
        .map(user => ({
          id: user.id,
          full_name: user.full_name,
        }));
    },
    userSavable() {
      return (
        this.selectedUser.email &&
        this.selectedUser.fname &&
        this.selectedUser.lname &&
        this.selectedUser.access
      );
    },
    newUserSavable() {
      return (
        this.newUserFirstName &&
        this.newUserLastName &&
        this.newUserEmail &&
        this.newUserAccess &&
        (this.newUserAccess !== 'basic' ||
          (this.newUserAccess === 'basic' &&
            !isEmpty(this.newUserAssignedClientIds))) &&
        (this.newUserAccess !== 'group' ||
          (this.newUserAccess === 'group' && this.newUserGroupId !== null))
      );
    },
  },
  methods: {
    get,
    getAccessRole(item) {
      if (!item.access) return '';

      const accessName = this.accessRoles.find(d => d.value === item.access);

      return accessName && accessName.name;
    },
    async sendSignInLink(user_id) {
      try {
        const response = await API.advisor.sendClientLoginInfo({
          userIds: [user_id],
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Sent sign-in link!',
          });
        }
      } catch (e) {
        console.log(e);
      }
    },
    async inviteUser() {
      try {
        this.tableLoading = true;
        this.userInviteDialogOpened = false;

        const response = await API.firm.inviteFirmUser({
          firstName: this.newUserFirstName,
          lastName: this.newUserLastName,
          email: this.newUserEmail,
          access: this.newUserAccess,
          userIds: this.newUserAssignedClientIds,
          phoneNumber: this.newUserPhoneNumber,
          groupId: this.newUserGroupId,
        });
        this.$store.dispatch('application/setSuccessNotification', {
          message: `Successfully sent an invitation to ${this.newUserEmail}`,
        });

        this.initInviteForm();
        this.$emit('refresh');
      } catch (e) {
        if (e.response.data.error || e.response.data.errors) {
          if (e.response.data.error.message === 'EMAIL_EXISTS') {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'Email is already taken.',
            });
          } else if (e.response.data.error.message === 'INVALID_EMAIL') {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'Email is invalid.',
            });
          } else {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'Something went wrong while adding the firm user.',
            });
          }
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: `Something went wrong while sending an invite to ${this.newUserEmail}. Please reach out to support for further assistance.`,
          });
        }
      } finally {
        this.tableLoading = false;
      }
    },
    initInviteForm() {
      this.newUserAccess = null;
      this.newUserEmail = null;
      this.newUserFirstName = null;
      this.newUserLastName = null;
      this.newUserPhoneNumber = null;
      this.newUserAssignedClientIds = [];
      this.newUserGroupId = null;
      this.$refs.form1.resetValidation();
    },
    async removeUser() {
      this.removeUserConfirmationDialogOpened = false;
      this.tableLoading = true;

      try {
        const response = await API.user.deleteClientFromAPI(
          this.selectedUser.id,
        );

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem removing the user',
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: "You've successfully removed the user.",
          });
          this.$emit('refresh');
        }
      } catch (err) {
        const message = err.response.data.error;

        this.$store.dispatch('application/setErrorNotification', {
          message: message || 'There was a problem removing the user',
        });
      }

      this.tableLoading = false;
    },
    openEditUserDialog(user) {
      this.selectedUser = {
        ...user,
        groupId: user.group ? user.group.id : null,
      };
      this.editUserDialogOpened = true;
    },
    openRemoveUserDialog(user) {
      this.selectedUser = { ...user };
      this.removeUserConfirmationDialogOpened = true;
    },
    openInviteClientDialog() {
      this.initInviteForm();
      this.userInviteDialogOpened = true;
    },
    async saveUser() {
      this.editUserDialogOpened = false;
      this.tableLoading = true;

      const {
        phone_number,
        fname,
        lname,
        email,
        access,
        groupId,
      } = this.selectedUser;

      try {
        const response = await API.user.updateUser({
          id: this.selectedUser.id,
          fname,
          lname,
          email,
          phone_number,
          access,
          group_id: groupId,
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          if (this.currentUser.id === this.selectedUser.id) {
            await this.$store.dispatch('getCurrentUser');
          }
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Information has been saved successfully.',
          });
          this.$emit('refresh');
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving information.',
        });
      } finally {
        this.tableLoading = false;
      }
    },
  },
};
</script>
